import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
  inject,
  signal,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import mapboxgl from 'mapbox-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import * as turf from '@turf/turf';
import {
  Subscription,
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { IGeoJSONCircle, IGeoJSONPoint } from 'modelos/src';
import { AuxiliaresModule } from '../auxiliares.module';
import { environment } from '../../../environments/environment';
import { applyDrawColor, buildDrawStyles } from './draw-styles';
import { DirectSelectSinCirculos, DrawCircleMode } from './circle-draw-mode';
import { IOsmResult, OsmService } from '../servicios/osm.service';
import { aplicarEtiquetasMapa } from '../mapbox/etiquetas-mapa';
import {
  IValorGeojsonForm,
  RADIO_CIRCULO_DEFECTO_M,
  TipoDibujo,
  featureToFormValue,
  formToFeature,
  valorVacio,
} from './geometry-mapper';

/**
 * Mapa para dibujar/editar una geometría dentro de un FormGroup con
 * controles `type` / `coordinates` / `radius` (radio en metros).
 *
 * Sin geometría inicial entra directo en modo dibujo; con geometría la
 * carga y permite editar vértices (direct_select). Los círculos usan un
 * modo custom de MapboxDraw (two-click) y se guardan como Point + radius.
 */
@Component({
  selector: 'app-map-draw-mapbox',
  imports: [AuxiliaresModule],
  templateUrl: './map-draw-mapbox.component.html',
  styleUrl: './map-draw-mapbox.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class MapDrawMapboxComponent
  implements OnInit, OnChanges, AfterViewInit, OnDestroy
{
  private ngZone = inject(NgZone);
  private osm = inject(OsmService);

  @ViewChild('mapContainer', { static: false })
  mapContainer!: ElementRef<HTMLElement>;

  @Input() form?: FormGroup;
  @Input() tipo: TipoDibujo = 'Polygon';
  @Input() color = '#ffcc33';
  @Input() centrarA?: IGeoJSONPoint | IGeoJSONCircle;
  /** Muestra el buscador de límites administrativos de OpenStreetMap */
  @Input() buscadorOsm = false;
  /**
   * Tipo de geometría detectado al importar un territorio OSM (Polygon o
   * MultiPolygon). El padre lo usa para sincronizar su `tipoDibujo` y que
   * las recargas/ediciones posteriores usen el tipo correcto.
   */
  @Output() tipoDetectado = new EventEmitter<TipoDibujo>();

  public dibujando = signal(false);
  public hayGeometria = signal(false);

  // Búsqueda OSM (Nominatim)
  public busquedaOsm = new FormControl('');
  public resultadosOsm: IOsmResult[] = [];
  public buscandoOsm = signal(false);
  private osmSub?: Subscription;

  private map: mapboxgl.Map | null = null;
  private draw: MapboxDraw | null = null;
  // Último valor escrito por este componente, para distinguir el eco de
  // valueChanges de un cambio externo real del form
  private ultimoValorEmitido: string | null = null;
  private formSub?: Subscription;

  private readonly MAP_ZOOM_KEY = 'mapDrawMapboxZoom';
  private readonly MAP_CENTER_KEY = 'mapDrawMapboxCenter';

  // ========== LIFECYCLE ==========

  ngOnInit(): void {
    this.formSub = this.form
      ?.get('coordinates')
      ?.valueChanges.subscribe((coords) => {
        if (JSON.stringify(coords ?? null) === this.ultimoValorEmitido) return;
        // Cambio externo: recargar la geometría en el mapa
        this.recargarDesdeForm();
      });

    this.suscribirBusquedaOsm();
  }

  ngAfterViewInit(): void {
    this.initMap();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['color'] && !changes['color'].firstChange) {
      this.aplicarColor();
    }

    if (changes['centrarA'] && this.centrarA?.coordinates) {
      this.panTo(this.centrarA.coordinates);

      // Point/Circle: colocar la geometría en la posición recibida (el
      // círculo conserva su radio; el de defecto sólo si no tenía)
      if (this.tipo === 'Point') {
        this.escribirForm({
          type: 'Point',
          coordinates: this.centrarA.coordinates,
          radius: null,
        });
        this.recargarDesdeForm();
      } else if (this.tipo === 'Circle') {
        this.escribirForm({
          type: 'Point',
          coordinates: this.centrarA.coordinates,
          radius: this.form?.get('radius')?.value || RADIO_CIRCULO_DEFECTO_M,
        });
        this.recargarDesdeForm();
      }
    }

    if (changes['tipo'] && !changes['tipo'].firstChange) {
      this.recargarDesdeForm();
    }
  }

  ngOnDestroy(): void {
    this.formSub?.unsubscribe();
    this.osmSub?.unsubscribe();
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }

  // ========== INICIALIZACIÓN ==========

  private initMap(): void {
    if (!this.mapContainer) return;

    // Crear el mapa fuera de la Angular Zone: el loop de render de Mapbox
    // (requestAnimationFrame, promises) dispararía change detection
    this.ngZone.runOutsideAngular(() => {
      this.map = new mapboxgl.Map({
        language: 'es', // Etiquetas del mapa base en español
        container: this.mapContainer.nativeElement,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: this.getSavedCenter(),
        zoom: this.getSavedZoom(),
        accessToken: environment.mapboxToken,
      });
      aplicarEtiquetasMapa(this.map);

      this.map.addControl(
        new mapboxgl.NavigationControl({
          showCompass: true,
          showZoom: true,
          visualizePitch: true,
        }),
        'bottom-right',
      );

      this.map.on('load', () => this.onMapLoaded());
      this.map.on('moveend', () => this.saveMapState());
    });
  }

  private onMapLoaded(): void {
    if (!this.map) return;

    this.crearControlDraw();

    // Eventos sobre el mapa: sobreviven a recreaciones del control
    this.map.on('draw.create', () => this.onDrawChange());
    this.map.on('draw.update', () => this.onDrawChange());
    this.map.on('draw.delete', () => {
      this.onDrawChange();
      this.entrarModoDibujo();
    });
    this.map.on('draw.modechange', () => this.onModeChange());

    this.recargarDesdeForm();
  }

  private crearControlDraw(): void {
    if (!this.map) return;
    this.draw = new MapboxDraw({
      displayControlsDefault: false,
      controls: {},
      styles: buildDrawStyles(this.color),
      modes: {
        ...MapboxDraw.modes,
        draw_circle: DrawCircleMode,
        direct_select: DirectSelectSinCirculos,
      },
    });
    this.map.addControl(this.draw);
  }

  // ========== SYNC MAPA → FORM ==========

  private onDrawChange(): void {
    if (!this.draw) return;
    const feature = this.draw.getAll().features[0];
    const valor = feature
      ? featureToFormValue(this.tipo, feature)
      : valorVacio(this.tipo);
    this.escribirForm(valor);
    this.actualizarEstadoModo();
  }

  private escribirForm(valor: IValorGeojsonForm): void {
    this.ultimoValorEmitido = JSON.stringify(valor.coordinates ?? null);
    // Los eventos de Draw corren fuera de la Angular Zone: sin ngZone.run
    // el consumidor no se entera del cambio hasta la próxima interacción
    this.ngZone.run(() => {
      this.form?.get('type')?.setValue(valor.type);
      this.form?.get('coordinates')?.setValue(valor.coordinates);
      this.form?.get('radius')?.setValue(valor.radius ?? null);
      this.hayGeometria.set(!!valor.coordinates);
    });
  }

  // ========== SYNC FORM → MAPA ==========

  /**
   * deleteAll con una feature seleccionada rompe el modo activo (queda
   * renderizando una selección muerta): deseleccionar antes de borrar.
   */
  private borrarTodo(): void {
    if (!this.draw) return;
    this.draw.changeMode('simple_select', { featureIds: [] });
    this.draw.deleteAll();
  }

  private recargarDesdeForm(tipoOverride?: TipoDibujo): void {
    if (!this.draw || !this.map) return;

    // Al importar OSM, el @Input `tipo` puede no haberse propagado todavía
    // (el padre lo actualiza vía tipoDetectado en el próximo ciclo de CD):
    // el override permite dibujar con el tipo correcto de inmediato.
    const tipo = tipoOverride ?? this.tipo;

    this.borrarTodo();
    const feature = formToFeature(tipo, this.leerForm());

    if (!feature) {
      this.ngZone.run(() => this.hayGeometria.set(false));
      this.entrarModoDibujo();
      return;
    }

    const [featureId] = this.draw.add(feature);
    this.ngZone.run(() => this.hayGeometria.set(true));
    this.fitBounds(feature);

    if (tipo === 'Point' || tipo === 'Circle') {
      // direct_select no aplica a puntos, y para círculos el override lo
      // redirige igual: seleccionar para permitir moverlos
      this.draw.changeMode('simple_select', { featureIds: [featureId] });
    } else {
      this.draw.changeMode('direct_select', { featureId });
    }
    this.actualizarEstadoModo();
  }

  private leerForm(): IValorGeojsonForm {
    return {
      type: this.form?.get('type')?.value,
      coordinates: this.form?.get('coordinates')?.value,
      radius: this.form?.get('radius')?.value,
    };
  }

  // ========== ACCIONES ==========

  private entrarModoDibujo(): void {
    if (!this.draw) return;

    const modos: Partial<Record<TipoDibujo, string>> = {
      Point: 'draw_point',
      LineString: 'draw_line_string',
      Polygon: 'draw_polygon',
      Circle: 'draw_circle',
      // MultiPolygon no se dibuja a mano: solo carga/edición
    };
    const modo = modos[this.tipo];
    if (modo) {
      this.draw.changeMode(modo as 'draw_polygon');
    }
    this.actualizarEstadoModo();
  }

  /** Descarta el trazo en curso y permite empezar de nuevo */
  public cancelarDibujo(): void {
    if (!this.draw) return;
    this.draw.trash();
    this.entrarModoDibujo();
  }

  public borrarDibujos(): void {
    if (!this.draw) return;
    this.borrarTodo();
    this.escribirForm(valorVacio(this.tipo));
    this.entrarModoDibujo();
  }

  // ========== BÚSQUEDA OSM ==========

  private suscribirBusquedaOsm(): void {
    this.osmSub = this.busquedaOsm.valueChanges
      .pipe(
        // Al seleccionar una opción, mat-autocomplete escribe el objeto
        // IOsmResult en el control: coercionar a '' evita romper el stream
        map((valor) => (typeof valor === 'string' ? valor.trim() : '')),
        filter((texto) => texto.length >= 3),
        debounceTime(500),
        distinctUntilChanged(),
        tap(() => this.buscandoOsm.set(true)),
        switchMap((texto) =>
          this.osm.buscar(texto).pipe(catchError(() => of([] as IOsmResult[]))),
        ),
      )
      .subscribe((resultados) => {
        // Solo límites administrativos (Polygon/MultiPolygon dibujables)
        this.resultadosOsm = (resultados || []).filter((r) =>
          this.osm.toValor(r.geojson),
        );
        this.buscandoOsm.set(false);
      });
  }

  public seleccionarOsm(resultado: IOsmResult): void {
    const valor = this.osm.toValor(resultado.geojson);
    if (!valor) {
      return;
    }
    // Avisar al padre para que sincronice su tipoDibujo (Polygon/MultiPolygon)
    this.tipoDetectado.emit(valor.type as TipoDibujo);
    this.escribirForm(valor);
    // Dibujar ya, con el tipo del resultado (sin esperar la propagación)
    this.recargarDesdeForm(valor.type as TipoDibujo);
    // Limpiar el buscador sin re-disparar la búsqueda
    this.resultadosOsm = [];
    this.busquedaOsm.setValue(resultado.display_name, { emitEvent: false });
  }

  /** Muestra el nombre del territorio en el input del autocomplete */
  public mostrarNombreOsm(resultado: IOsmResult | string): string {
    return typeof resultado === 'string'
      ? resultado
      : (resultado?.display_name ?? '');
  }

  // ========== HELPERS ==========

  private onModeChange(): void {
    // Escape (u otra salida de modo dibujo) sin geometría dejaría al
    // usuario sin forma de dibujar: volver a entrar en modo dibujo.
    // changeMode programático no dispara modechange, así que no recursa.
    if (
      this.draw?.getMode() === 'simple_select' &&
      this.draw.getAll().features.length === 0
    ) {
      setTimeout(() => this.entrarModoDibujo(), 0);
    }
    this.actualizarEstadoModo();
  }

  private actualizarEstadoModo(): void {
    const modo = this.draw?.getMode() ?? '';
    this.ngZone.run(() => this.dibujando.set(modo.startsWith('draw_')));
  }

  private aplicarColor(): void {
    if (!this.map || !this.draw) return;

    if (applyDrawColor(this.map, this.color)) return;

    // Fallback: si cambiaron los ids internos de la librería, recrear el
    // control con los estilos nuevos
    this.map.removeControl(this.draw);
    this.crearControlDraw();
    this.recargarDesdeForm();
  }

  private fitBounds(feature: GeoJSON.Feature): void {
    if (!this.map) return;
    const [minX, minY, maxX, maxY] = turf.bbox(feature);
    if (!isFinite(minX)) return;
    this.map.fitBounds([minX, minY, maxX, maxY], {
      padding: 50,
      maxZoom: 18,
    });
  }

  private panTo(coordinates: [number, number]): void {
    this.map?.flyTo({ center: coordinates, zoom: 18, duration: 1000 });
  }

  private getSavedCenter(): [number, number] {
    const saved = localStorage.getItem(this.MAP_CENTER_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      return [parsed.lng, parsed.lat];
    }
    return [-58.3816, -34.6037]; // Buenos Aires default
  }

  private getSavedZoom(): number {
    const saved = localStorage.getItem(this.MAP_ZOOM_KEY);
    return saved ? parseInt(saved, 10) : 13;
  }

  private saveMapState(): void {
    if (!this.map) return;
    const center = this.map.getCenter();
    localStorage.setItem(
      this.MAP_CENTER_KEY,
      JSON.stringify({ lng: center.lng, lat: center.lat }),
    );
    localStorage.setItem(this.MAP_ZOOM_KEY, this.map.getZoom().toString());
  }
}
